import React, { useMemo, useCallback } from 'react';
import i18next, { type Resource } from 'i18next';
import { I18nextProvider } from 'react-i18next';
import type { Node, PageProps } from 'gatsby';
import { FALLBACK_LANGUAGE, resolveLanguage } from './languages';

export interface LocaleNode extends Node {
  ns: string;
  data: Record<string, unknown>;
  language: string;
}

/** A locale node as page context carries it: the fields below, without Gatsby's node bookkeeping. */
export type PageLocale = Pick<LocaleNode, 'language' | 'ns' | 'data'>;

export type GatsbyPageContext = PageProps['pageContext'] & {
  i18n: {
    language: string;
  };
  locales?: PageLocale[];
};

interface I18nProviderProps {
  children: React.ReactNode;
  pageContext: GatsbyPageContext;
  locales?: PageLocale[];
}

/**
 * Reports a `t()` call against a namespace the page was not given.
 *
 * A production page carries only the namespaces gatsby/pageNamespaces.ts finds in its code. If that
 * scan ever misses one, `t()` would return the key string and, because `returnObjects: true` is
 * global, an array-valued call site would either throw an unclear error or render the raw key. This
 * names the namespace instead. During SSR (the build) it throws, so the build fails on the page that
 * needs it. In the browser it logs, because throwing there would blank a page that has already
 * rendered; a namespace only the browser reaches is one SSR could not catch.
 */
const createMissingNamespaceHandler = (given: ReadonlySet<string>) => {
  const reported = new Set<string>();

  return (_languages: readonly string[], ns: string) => {
    if (given.has(ns) || reported.has(ns)) {
      return;
    }
    reported.add(ns);

    const message =
      `Translation namespace "${ns}" was requested, but this page's context does not carry it. ` +
      'gatsby/pageNamespaces.ts decides which namespaces each page carries; make sure the component ' +
      'names it in a form that file reads.';

    if (typeof window === 'undefined') {
      throw new Error(message);
    }
    // eslint-disable-next-line no-console
    console.error(message);
  };
};

/**
 * Shared default for pages without locales (Gatsby's internal pages). A module-level constant rather
 * than a `= []` default, so the array keeps its identity across renders and useMemo below does not
 * rebuild the i18next instance on every render.
 */
const NO_LOCALES: PageLocale[] = [];

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, pageContext, locales = NO_LOCALES }) => {
  // Build i18n resources from locale nodes
  const buildResources = useCallback((localeNodes: PageLocale[]): Resource => {
    const resources: Resource = {};

    for (const node of localeNodes) {
      // Resolve node.language (the BCP 47 locale dir name, e.g. 'ja-JP') to its canonical
      // supported language code, tolerating any legacy bare codes still on disk.
      const { languageCode } = resolveLanguage(node.language);

      // Initialize language resources if needed
      if (!resources[languageCode]) {
        resources[languageCode] = {};
      }

      resources[languageCode][node.ns] = node.data;
    }

    return resources;
  }, []);

  const i18nInstance = useMemo(() => {
    const instance = i18next.createInstance();

    // Process locale nodes
    const resources = buildResources(locales);

    instance.init({
      returnObjects: true,
      debug: process.env.NODE_ENV === 'development',
      lng: pageContext.i18n.language,
      fallbackLng: FALLBACK_LANGUAGE,
      resources,
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
      // saveMissing only turns on missingKeyHandler; there is no backend to save to.
      saveMissing: true,
      missingKeyHandler: createMissingNamespaceHandler(new Set(locales.map((node) => node.ns))),
    });

    return instance;
  }, [locales, pageContext.i18n.language, buildResources]);

  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
};
